import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { ExamdojoAgGridModule } from '@examdojo/ag-grid';
import { connectState } from '@examdojo/angular/util';
import { provideFaIcons } from '@examdojo/icons';
import { isNotNullish } from '@examdojo/util/nullish';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BodyScrollEvent,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  RowClickedEvent,
} from 'ag-grid-community';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  pairwise,
  Subject,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AssetTreeMenuOption, AssetTreeNode, AssetTreeNodeType } from '../../model';
import { AssetExplorerService } from '../asset-explorer.service';
import {
  closestContainer,
  closestParent,
  filterOutCommonNodes,
  findInTree,
  getNodeRouterLink,
  getVisiblePathParts,
  isContainerNode,
  isVisibleNode,
} from '../asset-tree-builder';
import { AssetTreeSettingsService } from '../asset-tree-settings.service';
import { AssetTreeSortType } from '../asset-tree-toolbar/asset-tree-toolbar.model';
import { expandWithParents } from './asset-tree-ag-grid.utils';
import { AssetTreeDnDService } from './asset-tree-dnd.service';
import { AssetTreeNodeCellRendererComponent } from './asset-tree-node-cell-renderer/asset-tree-node-cell-renderer.component';
import { AssetTreeUiService } from './asset-tree-ui.service';

const DEFAULT_AG_GRID_COLUMN_KEY = 'ag-Grid-AutoColumn';
const ALWAYS_EXPANDED_NODE_TYPES = [AssetTreeNodeType.AssetsContainer, AssetTreeNodeType.TopicLevel2];

@UntilDestroy()
@Component({
  selector: 'exam-asset-tree',
  imports: [ExamdojoAgGridModule, MatTooltipModule],
  templateUrl: './asset-tree.component.html',
  styleUrls: ['./asset-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AssetTreeDnDService],
})
export class AssetTreeComponent {
  constructor(
    private readonly assetExplorerService: AssetExplorerService,
    private readonly assetTreeToolbarService: AssetTreeSettingsService,
    private readonly assetTreeDnDService: AssetTreeDnDService,
    private readonly assetTreeUiService: AssetTreeUiService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    merge(
      this.updateCellsOnTreeChange(),
      this.triggerAgGridSortingOnSortChange(),
      this.expandAndFocusOnNodeOnActiveNodeChange(),
      this.clearSelectionOnBlur(),
      this.updateSelectionOnRowClicks(),
      //this.revealAssetOnRevealAssetEvent(),
      this.createPlaceholderDirectoryOnDemand(),
    )
      .pipe(untilDestroyed(this))
      .subscribe();

    provideFaIcons([faStar]);
  }

  /**
   * An anchor node is used to keep track of the node based on which the selection should be extended or reduced.
   */
  private selectionAnchorNode: IRowNode<AssetTreeNode> | null = null;
  private readonly treeNodes$ = this.assetExplorerService.nodes$;

  private readonly rowClicked$$ = new Subject<RowClickedEvent<AssetTreeNode>>();

  private lastEditingCellTimeStamp?: number;

  private readonly oldFocusedNode$: Observable<AssetTreeNode | undefined> = this.assetExplorerService.focusedNode$.pipe(
    pairwise(),
    map(([oldFocusedNode, _newFocusedNode]) => oldFocusedNode),
  );

  gridApi?: GridApi<AssetTreeNode>;

  readonly gridOptions$: Observable<GridOptions<AssetTreeNode>> = this.assetExplorerService.readonlyMode$.pipe(
    map((readonly) => this.getGridOptions(readonly)),
  );

  readonly state = connectState({
    root: this.assetExplorerService.rootNode$,
    treeNodes: this.treeNodes$,
    sortType: this.assetTreeToolbarService.sortType$,
    newDirectoryPlaceholder: this.assetExplorerService.newDirectoryPlaceholder$,
    gridOptions: this.gridOptions$,
    readonly: this.assetExplorerService.readonlyMode$,
  });

  getGridOptions(readonly: boolean): GridOptions<AssetTreeNode> {
    return {
      treeData: true,
      rowSelection: {
        mode: 'singleRow',
        checkboxes: false,
      },
      //if you change it, please change '$asset-tree-row-height' in _variables.scss
      rowHeight: 33,
      suppressGroupRowsSticky: true,
      rowClass: 'node-row',
      defaultColDef: {
        flex: 1,
      },
      columnDefs: [],
      animateRows: true,
      getDataPath: getVisiblePathParts,
      getRowId: (params: GetRowIdParams<AssetTreeNode>) => params.data.path,

      // Increase the buffer to make the scrolling smoother.
      rowBuffer: 50,

      onRowDataUpdated: (params) => {
        // Always expand all assets containing some children.
        params.api.forEachNode((node) => {
          if (node.data?.asset && Object.keys(node.data.children).length > 0) {
            node.setExpanded(true);
          }
        });
      },

      overlayNoRowsTemplate: readonly
        ? 'No assets to show'
        : "<div><p>You'll see your data pipeline assets here.</p><p>Start by creating your first asset!</p></div>",
      overlayLoadingTemplate: 'Loading assets...',

      autoGroupColumnDef: {
        cellRenderer: AssetTreeNodeCellRendererComponent,
        sort: 'asc',
        sortable: true,
        comparator: (valueA: string, valueB: string, nodeA, nodeB) => {
          if (!nodeA.data || !nodeB.data) {
            return valueA.localeCompare(valueB);
          }

          // move directories to the top.
          if (!nodeA.data.asset && !nodeB.data.asset) {
            return nodeA.data?.modelName.toLowerCase().localeCompare(nodeB.data.modelName.toLowerCase());
          }
          if (!nodeA.data.asset) {
            return -1;
          }
          if (!nodeB.data.asset) {
            return 1;
          }

          switch (this.state.sortType) {
            case AssetTreeSortType.Default: {
              return (nodeA.data.order ?? 0) - (nodeB.data.order ?? 0);
            }
            case AssetTreeSortType.Alphabetically: {
              // sort by name combined with feature type for assets.
              return nodeA.data.modelName.toLowerCase().localeCompare(nodeB.data.modelName.toLowerCase());
            }
          }
        },
        suppressKeyboardEvent: (params) => {
          if (params.event.code === 'Enter' && this.gridApi?.getEditingCells().length) {
            this.gridApi.stopEditing();
            return true;
          }

          if (this.gridApi?.getEditingCells().length) {
            return false;
          }

          // prevent default ag-grid action + prevent default browser action.
          params.event.preventDefault();
          return true;
        },
        cellClassRules: {
          'dragging-target': (params) => this.assetTreeDnDService.isPotentialDropTarget(params.node),
        },
        // editable: (params) => !params.data?.asset,
        // cellEditor: AssetTreeNodeCellEditorComponent,
      },

      readOnlyEdit: true,
      onCellEditingStopped: (params) => {
        this.lastEditingCellTimeStamp = Date.now();
        if (params.node.data && this.state.newDirectoryPlaceholder === params.node.data.parent?.path) {
          this.gridApi?.applyTransaction({
            remove: [params.node.data],
          });
        }
        if (params.node.rowIndex !== null) {
          params.api.setFocusedCell(params.node.rowIndex, params.column);
        }

        this.assetExplorerService.clearNewDirectoryPlaceholder();
      },

      onCellEditRequest: (params) => {
        if (!params.newValue || params.newValue === params.oldValue) {
          return;
        }

        const node = params.node?.data;
        if (!params.node || !node) {
          return;
        }

        const newName = params.newValue.trim();
        const pathParts = node.path.split('/');
        const newPath = [...pathParts.slice(0, -1), newName].join('/');

        if (this.state.newDirectoryPlaceholder) {
          this.assetExplorerService.addTemporaryDirectory(newPath);
          return;
        }

        this.assetExplorerService.move([node], newPath, node.path);

        // TODO: Restore the originally expanded nodes.
      },

      allowContextMenuWithControlKey: true,
      suppressClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,

      groupDefaultExpanded: 2,

      onCellKeyDown: (agGridEvent) => {
        const event = agGridEvent.event as KeyboardEvent;
        const selection = this.getUniqueSelectionNodes();
        const affectedNodes = selection.length ? selection : [agGridEvent.node.data].filter(isNotNullish);

        // Prevent custom actions during cell editing and short after the editing.
        if (
          agGridEvent.api.getEditingCells().length ||
          (this.lastEditingCellTimeStamp && Date.now() - this.lastEditingCellTimeStamp < 100)
        ) {
          return;
        }

        const agGridFocusedNode = agGridEvent.node;

        switch (event.key) {
          case 'ArrowRight':
            if (!event.shiftKey) {
              this.descendTowardsLeaves(agGridFocusedNode);
            }
            break;

          case 'ArrowLeft':
            if (!event.shiftKey) {
              this.ascendTowardsRoot(agGridFocusedNode);
            }
            break;

          case 'ArrowUp':
            this.goToNode(agGridFocusedNode, -1, event.shiftKey);
            break;

          case 'ArrowDown':
            this.goToNode(agGridFocusedNode, +1, event.shiftKey);
            break;

          case ' ':
          case 'Enter':
            if (event.shiftKey) {
              // only toggle selection.
              agGridFocusedNode.setSelected(!agGridFocusedNode.isSelected());
            } else {
              this.toggleOrActivateNode(agGridFocusedNode);
            }
            break;

          case 'F2':
            if (readonly || !agGridFocusedNode.data || isContainerNode(agGridFocusedNode.data)) {
              return;
            }

            this.moveOrRenameNode(agGridFocusedNode.data);
            break;

          case 'Escape':
            this.gridApi?.deselectAll();
            this.selectionAnchorNode = null;
            //this.assetExplorerService.clearClipboard();
            break;

          case 'a':
            if (event.metaKey || event.ctrlKey) {
              event.stopPropagation();
              event.preventDefault();

              this.gridApi?.selectAll();
            }
            break;

          // case 'x':
          //   if (readonly) {
          //     return;
          //   }
          //
          //   if (event.metaKey || event.ctrlKey) {
          //     event.stopPropagation();
          //     event.preventDefault();
          //
          //     this.assetExplorerService.cutToClipboard(affectedNodes);
          //   }
          //   break;

          // case 'v':
          //   if (readonly) {
          //     return;
          //   }
          //
          //   if (event.metaKey || event.ctrlKey) {
          //     event.stopPropagation();
          //     event.preventDefault();
          //
          //     if (agGridFocusedNode.data) {
          //       this.assetExplorerService.pasteFromClipboard(agGridFocusedNode.data);
          //       agGridFocusedNode.setExpanded(true);
          //     } else {
          //       this.assetExplorerService.clearClipboard();
          //     }
          //   }
          //   break;

          case 'Backspace':
            if (readonly) {
              return;
            }

            if ((event.metaKey || event.ctrlKey) && agGridFocusedNode.data) {
              this.assetExplorerService.deleteNodes(affectedNodes);
            }
            break;
        }
      },

      onCellFocused: (event) => {
        if (event.rowIndex === null) {
          return;
        }

        const row = this.gridApi?.getDisplayedRowAtIndex(event.rowIndex);

        if (!row) {
          return;
        }

        const data = row.data;

        this.assetExplorerService.setFocusedNode(data);
      },

      rowDragEntireRow: !readonly,
      rowDragMultiRow: !readonly,

      // TODO: Ideally we should disable dragging when editing, but it's not possible(?) to do it with ag-grid.
      onRowDragEnter: (event) => {
        this.gridApi?.stopEditing();
        this.assetTreeDnDService.onRowDragMove(event);
      },

      onRowDragMove: this.assetTreeDnDService.onRowDragMove,
      onRowDragLeave: this.assetTreeDnDService.onRowDragLeave,
      onRowDragEnd: this.assetTreeDnDService.onRowDragEnd,

      popupParent: this.document.body,

      onRowClicked: (event) => {
        this.rowClicked$$.next(event);
      },
    };
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  onGridScrolled(event: BodyScrollEvent) {
    const scrolled = event.top !== 0 && event.direction === 'vertical';

    this.assetTreeUiService.setScrolled(scrolled);
  }

  getContextMenuItems = (_params: GetContextMenuItemsParams<AssetTreeNode>): AssetTreeMenuOption[] => {
    return [];
    /*const rightClickedNode = params.node?.data;

    const selection = (this.gridApi?.getSelectedNodes() ?? []).map((node) => node.data).filter(isNotNullish);
    const affectedNodes = rightClickedNode
      ? selection.includes(rightClickedNode)
        ? selection
        : [rightClickedNode]
      : [];

    const hasLatestValidJob = (node: AssetTreeNode) => {
      const entity = node.asset;
      const query = entity && this.entityResolverService.getServiceDefinitionByEntityType(entity.entityType).query;
      return query && isJobbableEntityQuery(query) && query.getLatestValidJob(entity.$id);
    };

    const getAsset = (node: AssetTreeNode): Asset | null | undefined => {
      const entity = node.asset;
      const query = entity && this.entityResolverService.getServiceDefinitionByEntityType(entity.entityType).query;
      return entity && query?.getUIEntity(entity.$id);
    };

    const someAssetsNotStarred = affectedNodes.some((node) => !getAsset(node)?.starred);

    const isFavoriteAssetNode = (node: AssetTreeNode) => node.nodeType === AssetTreeNodeType.FavoriteAsset;

    const contextMenuItems: AssetTreeMenuOption[] = [
      {
        name: 'Open In',
        icon: `<i class="fa-light fa-folder-open"></i>`,
        hidden: !rightClickedNode?.asset,
        subMenu:
          rightClickedNode && rightClickedNode.asset
            ? this.assetExplorerSettingsService.getOpenInOptions(getAsset(rightClickedNode)!)
            : [],
      },
      ...getLineageFocusedModeMenuItems(),
      'separator',
      {
        name: someAssetsNotStarred ? 'Add to favorites' : 'Remove from favorites',
        icon: `<i class="fa-star ${someAssetsNotStarred ? 'fa-light' : 'fa-solid'}"></i>`,
        hidden: this.state.readonly || affectedNodes.every((node) => !node.asset),
        action: () => {
          const newValue = someAssetsNotStarred;
          for (const node of affectedNodes) {
            if (node.asset) {
              this.entityAggregationService.setStarred(node.asset.$id, newValue);
            }
          }
        },
      },
      {
        name: 'Rename',
        icon: `<i class="fa-light fa-pen-line"></i>`,
        hidden: this.state.readonly || !rightClickedNode || isContainerNode(rightClickedNode),
        action: () => this.moveOrRenameNode(rightClickedNode!),
      },
      'separator',
      {
        name: 'Generate Staging Model',
        icon: `<img border="0" width="14" height="14" src="${sqlModelIconWhite}"/>`,
        hidden:
          this.state.readonly ||
          !affectedNodes.some((node) => {
            return (
              node.asset &&
              node.asset?.entityType &&
              (node.asset.entityType === EntityType.SourceTable || node.asset.entityType === EntityType.Seed) &&
              isEntityTypeJobbable(node.asset.entityType) &&
              hasLatestValidJob(node)
            );
          }),
        action: () => {
          for (const node of affectedNodes) {
            if (
              !node.asset ||
              !node.asset?.entityType ||
              !(node.asset.entityType === EntityType.SourceTable || node.asset.entityType === EntityType.Seed) ||
              !isEntityTypeJobbable(node.asset.entityType) ||
              !hasLatestValidJob(node)
            ) {
              continue;
            }

            this.entityActionDelegationService.delegateAction({
              type: EntityActionType.GenerateStagingModel,
              payload: {
                id: node.asset?.$id as EntityID<typeof EntityType.SourceTable> | EntityID<typeof EntityType.Seed>,
              },
            });
          }
        },
      },
      {
        name: 'Sync columns',
        icon: `<i class="fa-light fa-arrows-rotate"></i>`,
        hidden:
          this.state.readonly || affectedNodes.every((node) => !node.asset || !isImpexEntityStoreModel(node.asset)),
        action: async () => {
          for (const node of affectedNodes) {
            if (!node.asset || !isImpexEntityStoreModel(node.asset)) {
              continue;
            }

            const service = this.entityResolverService.getServiceDefinitionByEntityType(node.asset!.entityType).service;
            assertImpexEntityService(service);
            await service.syncSchema(node.asset!.$id);
          }
        },
      },
      {
        name: 'Sync columns for all',
        icon: `<i class="fa-light fa-arrows-spin"></i>`,
        hidden:
          this.state.readonly || affectedNodes.every((node) => !node.asset || !isImpexEntityStoreModel(node.asset)),
        action: async () => {
          ImpexEntityTypes.map((type) => this.entityResolverService.getServiceDefinitionByEntityType(type)).forEach(
            (serviceDef) => {
              const entities = serviceDef.service.query.getAllEntities();
              const service = serviceDef.service;
              assertImpexEntityService(service);
              // the action is async but we don't have to wait for it.
              entities.forEach((entity) => service.syncSchema(entity.$id));
            },
          );
        },
      },
      {
        name: 'Add Table',
        icon: `<i class="fa-light fa-plus"></i>`,
        hidden:
          this.state.readonly ||
          !rightClickedNode ||
          !rightClickedNode.asset ||
          !isSourceConfigEntityStoreModel(rightClickedNode.asset) ||
          !this.sourceConfigEntityQuery.isCDataSourceConfig(rightClickedNode.asset) ||
          !this.featureFlagService.get(Y42FeatureFlag.CDataSourcesCustomQuery),
        action: () => {
          const id = rightClickedNode?.asset?.$id;
          assertNonNullable(id, 'id');

          this.entityActionDelegationService.delegateAction({
            type: EntityActionType.OpenAssetCreateDialog,
            payload: {
              entityType: EntityType.SourceTable,
              meta: {
                source: id,
              },
              useDedicatedDialogOnly: true,
            },
          });
        },
      },
      'separator',
      {
        name: 'Cut',
        icon: `<i class="fa-light fa-scissors"></i>`,
        shortcut: this.getShortcutIcon(['command+x', 'ctrl+x']),
        hidden:
          this.state.readonly ||
          !rightClickedNode ||
          affectedNodes.some((node) => isContainerNode(node) || isFavoriteAssetNode(node)),
        disabled: !rightClickedNode || isChildAssetNode(rightClickedNode),
        action: () => {
          this.assetExplorerService.cutToClipboard(filterOutCommonNodes(affectedNodes));
        },
      },
      {
        name: 'Copy',
        icon: `<i class="fa-light fa-copy"></i>`,
        hidden: !rightClickedNode || isContainerNode(rightClickedNode),
        subMenu: [
          {
            name: 'Asset name',
            icon: `<i class="fa-light fa-brackets-square"></i>`,
            hidden: !rightClickedNode || !rightClickedNode.asset,
            action: () => {
              navigator.clipboard.writeText(rightClickedNode!.asset!.name);
            },
          },
          {
            name: 'Link to asset',
            icon: `<i class="fa-light fa-link-simple"></i>`,
            hidden: !rightClickedNode || !rightClickedNode.asset,
            action: () => {
              navigator.clipboard.writeText(
                this.routerService.getUrlWithDomain({
                  org: ACTIVE,
                  space: ACTIVE,
                  appMode: ACTIVE,
                  view: ACTIVE,
                  branch: ACTIVE,
                  asset: rightClickedNode?.asset?.$id,
                }),
              );
            },
          },
          {
            name: 'Relative path',
            icon: `<i class="fa-light fa-slash-forward"></i>`,
            hidden: !rightClickedNode,
            action: () => {
              navigator.clipboard.writeText(
                rightClickedNode!.asset
                  ? rightClickedNode!.asset.$path
                  : getPathRelativeToAssetsContainer(rightClickedNode!),
              );
            },
          },
        ],
      },
      {
        name: 'Paste',
        icon: `<i class="fa-light fa-paste"></i>`,
        shortcut: this.getShortcutIcon(['command+v', 'ctrl+v']),
        hidden:
          this.state.readonly ||
          !rightClickedNode ||
          isContainerNode(rightClickedNode) ||
          isFavoriteAssetNode(rightClickedNode),
        disabled: this.assetExplorerService.isClipboardEmpty(),
        action: () => {
          params.node!.setExpanded(true);
          this.assetExplorerService.pasteFromClipboard(rightClickedNode!);
        },
      },
      'separator',
      {
        name: 'Expand All',
        icon: `<i class="fa-light fa-arrows-from-dotted-line"></i>`,
        action: () => this.gridApi?.expandAll(),
      },
      {
        name: 'Collapse All',
        icon: `<i class="fa-light fa-arrows-to-line"></i>`,
        action: () => this.gridApi?.collapseAll(),
      },
      'separator',
      {
        name: 'Delete',
        icon: `<i class="fa-light fa-trash-can"></i>`,
        hidden: this.state.readonly || !affectedNodes.length || affectedNodes.some(isContainerNode),
        action: () => {
          this.assetExplorerService.deleteNodes(filterOutCommonNodes(affectedNodes));
        },
        cssClasses: ['dangerous-action'],
      },
    ];

    // cleans up the excessive separators and undefined values in a generic way,
    // so we don't have to worry about it when changing the menu/submenus in the future.
    return this.cleanContextMenuItems(contextMenuItems);*/
  };

  private cleanContextMenuItems(contextMenuItems: AssetTreeMenuOption[]): AssetTreeMenuOption[] {
    const cleanContextMenuItems: AssetTreeMenuOption[] = [];

    // Remove leading, and duplicated separators.
    for (const item of contextMenuItems) {
      if (typeof item !== 'string' && item.hidden) {
        continue;
      }

      if (item !== 'separator') {
        cleanContextMenuItems.push(item);
        continue;
      }

      if (cleanContextMenuItems.length > 0 && cleanContextMenuItems.at(-1) !== 'separator') {
        cleanContextMenuItems.push(item);
      }
    }

    // Remove trailing separators.
    while (cleanContextMenuItems.at(-1) === 'separator') {
      cleanContextMenuItems.pop();
    }

    // Clean recursively all submenus.
    for (const item of cleanContextMenuItems) {
      if (typeof item === 'object' && item?.subMenu) {
        item.subMenu = this.cleanContextMenuItems(item.subMenu);
      }
    }

    return cleanContextMenuItems;
  }

  /*private getShortcutIcon(keySequences: [string, string]) {
    return this.keyboardShortcutsService.getHtmlLabelsForSequence(keySequences[0]).join('');
  }*/

  private moveOrRenameNode(node: AssetTreeNode) {
    if (node.asset) {
      this.assetExplorerService.renameNode(node);
      return;
    }

    const rowNode = this.gridApi?.getRowNode(node.path);

    if (!rowNode || rowNode.rowIndex === null) {
      return;
    }

    this.gridApi?.startEditingCell({
      rowIndex: rowNode.rowIndex,
      colKey: DEFAULT_AG_GRID_COLUMN_KEY,
    });
  }

  private descendTowardsLeaves(fromNode: IRowNode<AssetTreeNode>) {
    const column = this.gridApi?.getFocusedCell()?.column;

    if (!fromNode.expanded && fromNode.data && !ALWAYS_EXPANDED_NODE_TYPES.includes(fromNode.data.nodeType)) {
      fromNode.setExpanded(true);
      return;
    }

    if (fromNode.rowIndex !== null && column && fromNode.childrenAfterSort?.length) {
      // using rowIndex is safer to use than other methods, because it takes into account the current sort order and filters.
      const targetNode = this.gridApi?.getDisplayedRowAtIndex(fromNode.rowIndex + 1);
      this.gridApi?.setFocusedCell(fromNode.rowIndex + 1, column);
      this.selectionAnchorNode = targetNode ?? null;
    }
  }

  private ascendTowardsRoot(fromNode: IRowNode<AssetTreeNode>) {
    const column = this.gridApi?.getFocusedCell()?.column;

    if (fromNode.expanded && fromNode.data && !ALWAYS_EXPANDED_NODE_TYPES.includes(fromNode.data.nodeType)) {
      fromNode.setExpanded(false);
      return;
    }

    if (!fromNode.data) {
      return;
    }

    const parentNode = closestParent(fromNode.data.parent, (node) => isVisibleNode(node));
    const targetNode = parentNode && this.gridApi?.getRowNode(parentNode.path);

    if (targetNode && targetNode.rowIndex !== null && column) {
      // using rowIndex is safer to use than other methods, because it takes into account the current sort order and filters.
      this.gridApi?.setFocusedCell(targetNode.rowIndex, column);
      this.selectionAnchorNode = targetNode ?? null;
    }
  }

  private goToNode(fromNode: IRowNode<AssetTreeNode>, distance: number, withSelection: boolean) {
    const column = this.gridApi?.getFocusedCell()?.column;

    if (fromNode.rowIndex === null || !column) {
      return;
    }

    const targetNode = this.gridApi?.getDisplayedRowAtIndex(fromNode.rowIndex + distance);

    if (!targetNode || !targetNode.data || targetNode?.rowIndex === null) {
      return;
    }

    // For selecting across various containers
    // let's clear the whole selection as it does not make sense.
    if (this.isConflictingWithCurrentSelection(targetNode.data)) {
      this.selectionAnchorNode = targetNode;
      this.gridApi?.deselectAll();
    }

    if (isContainerNode(targetNode.data)) {
      this.gridApi?.deselectAll();
    }

    this.gridApi?.setFocusedCell(targetNode.rowIndex, column);

    if (!withSelection || !this.selectionAnchorNode || this.selectionAnchorNode.rowIndex === null) {
      this.selectionAnchorNode = targetNode;
      return;
    }

    // Select all nodes that are from the startSelectionNode to the targetNode.
    // Deselect nodes that are from the startSelectionNode to the fromNode when going backwards.
    // Example:
    // Start items: (1, 2, 3, 4, 5);
    // 1. user selects 3,
    // Result: (1, 2, [3], 4, 5)
    // 2. user presses shift and selects 5.
    // Result: (1, 2, [3, 4, 5])
    // 3. user presses shift and selects 1.
    // Result: ([1, 2, 3], 4, 5)

    const startIndex = this.selectionAnchorNode.id === fromNode.id ? 1 : 0;

    for (let index = startIndex; index <= Math.abs(distance); index++) {
      const nextNode = this.gridApi?.getDisplayedRowAtIndex(fromNode.rowIndex + (distance > 0 ? index : -index));

      if (!nextNode || nextNode.rowIndex === null) {
        continue;
      }

      const isGoingBack =
        (this.selectionAnchorNode.rowIndex < nextNode.rowIndex && distance < 0) ||
        (this.selectionAnchorNode.rowIndex > nextNode.rowIndex && distance > 0);

      nextNode.setSelected(!isGoingBack);
    }

    targetNode.setSelected(true);
  }

  private isConflictingWithCurrentSelection(node: AssetTreeNode): boolean {
    const firstSelectedNode = this.gridApi?.getSelectedNodes()[0]?.data;
    const selectionContainer = closestContainer(firstSelectedNode);
    const targetContainer = closestContainer(node);

    return !!selectionContainer && selectionContainer !== targetContainer;
  }

  private toggleOrActivateNode(node: IRowNode<AssetTreeNode>) {
    const nodeData = node.data;

    if (node.isExpandable() && nodeData?.nodeType && !ALWAYS_EXPANDED_NODE_TYPES.includes(nodeData.nodeType)) {
      node.setExpanded(!node.expanded);
      return;
    }

    this.gridApi?.deselectAll();
    node.setSelected(true);

    if (!nodeData) {
      return;
    }

    const linkPath = getNodeRouterLink(nodeData);

    if (!linkPath) {
      return;
    }

    return this.router.navigate([linkPath], { relativeTo: this.route });
  }

  private expandAndFocusOnNodeOnActiveNodeChange() {
    return this.assetExplorerService.activeNode$.pipe(
      distinctUntilChanged((a, b) => a?.path === b?.path),
      debounceTime(0),
      tap((activeNode) => this.assetExplorerService.setFocusedNode(activeNode)),
      map((activeNode) => activeNode && this.gridApi && this.gridApi.getRowNode(activeNode.path)),
      filter(Boolean),
      tap(expandWithParents),
      debounceTime(0),
      tap((agGridNode) => this.ensureNodeVisible(agGridNode)),
    );
  }

  private ensureNodeVisible(node: IRowNode<AssetTreeNode>) {
    if (!this.gridApi) {
      return;
    }

    let previousIndex: number | null = null;

    // For some reason calling it the first time may give incorrect result.
    // Maybe it's because of the tree structure that is unfolding.
    while (previousIndex !== node.rowIndex) {
      previousIndex = node.rowIndex;
      this.gridApi.ensureNodeVisible(node);
    }
  }

  private updateCellsOnTreeChange() {
    return this.treeNodes$.pipe(
      // TODO: replace only changed cells.
      tap(() => this.gridApi?.refreshCells({ force: true })),
    );
  }

  private triggerAgGridSortingOnSortChange() {
    return this.assetTreeToolbarService.sortType$.pipe(
      tap(() => {
        this.gridApi?.onSortChanged();
      }),
    );
  }

  private updateSelectionOnRowClicks() {
    return this.rowClicked$$.pipe(
      withLatestFrom(this.oldFocusedNode$),
      tap(([rowClickedEvent, oldFocusedNode]) => {
        const oldFocusedAgGridNode = oldFocusedNode ? this.gridApi?.getRowNode(oldFocusedNode?.path) ?? null : null;

        if (this.gridApi?.getEditingCells().length) {
          return;
        }

        const event = rowClickedEvent.event as MouseEvent;

        if (
          event.shiftKey &&
          // When these are nullish, fallback to the "normal" single select behavior.
          rowClickedEvent.node.rowIndex !== null &&
          oldFocusedAgGridNode &&
          oldFocusedAgGridNode.rowIndex !== null
        ) {
          this.goToNode(oldFocusedAgGridNode, rowClickedEvent.node.rowIndex - oldFocusedAgGridNode.rowIndex, true);
          return;
        }

        this.selectionAnchorNode = rowClickedEvent.node;

        const node = rowClickedEvent.node.data;
        if (!node) {
          return;
        }

        if (node.nodeType === AssetTreeNodeType.AssetsContainer) {
          return;
        }

        if (event.ctrlKey || event.metaKey) {
          if (!isContainerNode(node) && !this.isConflictingWithCurrentSelection(node)) {
            rowClickedEvent.node.setSelected(!rowClickedEvent.node.isSelected());
          }

          event.preventDefault();
          return;
        }

        // Enforce always opened asset nodes.
        if (ALWAYS_EXPANDED_NODE_TYPES.includes(node.nodeType)) {
          rowClickedEvent.node.setExpanded(true);
        } else {
          rowClickedEvent.node.setExpanded(!rowClickedEvent.node.expanded);
        }

        if (!isContainerNode(node) && !this.isConflictingWithCurrentSelection(node)) {
          rowClickedEvent.node.setSelected(true, true);
        }

        event.preventDefault();
      }),
    );
  }

  private clearSelectionOnBlur() {
    return fromEvent<MouseEvent>(document, 'click').pipe(
      tap((evt) => {
        if (!(evt.target instanceof Element)) {
          return;
        }

        const isRowEvent = evt.target.closest('exam-asset-tree-node-cell-renderer');

        if (!isRowEvent) {
          this.gridApi?.deselectAll();
          this.assetExplorerService.setFocusedNode(undefined);
        }
      }),
    );
  }

  private getUniqueSelectionNodes(): AssetTreeNode[] {
    const selection = this.gridApi?.getSelectedNodes();

    if (!selection) {
      return [];
    }

    return filterOutCommonNodes(selection.map((node) => node.data).filter(isNotNullish));
  }

  /*private revealAssetOnRevealAssetEvent() {
    return this.entityActionDelegationService.selectAction(EntityActionType.RevealInAssetTree).pipe(
      withLatestFrom(this.assetExplorerService.sourceDirectoryNode$),
      map(([payload, sourceDirectoryNode]) => {
        if (!sourceDirectoryNode) {
          return;
        }
        const node: AssetTreeNode | undefined =
          payload.by === 'id'
            ? this.getNodeByAssetID(sourceDirectoryNode, payload.id)
            : this.getNodeByDisplayPath(sourceDirectoryNode, payload.displayPath, payload.entityType);

        const rowNode = node?.path && this.gridApi?.getRowNode(node.path);

        return rowNode;
      }),
      filter(Boolean),
      tap((rowNode) => {
        expandWithParents(rowNode);
        this.gridApi?.ensureNodeVisible(rowNode);
      }),
      // Add a small delay. Otherwise the flashCells animation is not visible.
      delay(250),
      tap((rowNode) => {
        this.gridApi?.setFocusedCell(rowNode.rowIndex!, DEFAULT_AG_GRID_COLUMN_KEY);
        this.gridApi?.flashCells({ rowNodes: [rowNode] });
      }),
    );
  }*/

  private createPlaceholderDirectoryOnDemand() {
    return this.assetExplorerService.newDirectoryPlaceholder$.pipe(
      // Create the placeholder in the next tick to prevent closing the editable area.
      debounceTime(0),
      filter(isNotNullish),
      tap((parentDirPath) => {
        if (!this.gridApi) {
          return;
        }

        const parentRowNode = this.gridApi.getRowNode(parentDirPath);
        const parentNode = findInTree(this.state.root, (node) => node.path === parentDirPath);

        if (!parentNode) {
          return;
        }

        // The row node does not exists for root node.
        if (parentRowNode) {
          expandWithParents(parentRowNode);
        }

        // Create the node using the ag-grid API instead of recreating
        // the whole tree with new cell that will be edited later.
        // This way we can start editing the cell immediately.
        // The downside is that we have to manually handle adding and removing that node.
        const transaction = this.gridApi.applyTransaction({
          add: [
            {
              nodeType: AssetTreeNodeType.TemporaryDirectory,
              modelName: '',
              uiName: '',
              children: {},
              parent: parentNode,
              path: `${parentDirPath}/`,
            },
          ],
        });

        const added = transaction?.add?.[0];

        if (!added || added.rowIndex === null) {
          return;
        }

        // Blur the active element (which can be the toolbar add icon) to prevent a strange bug
        // as for some reason despite the new cell being focused, the `enter` also triggers the toolbar action
        // that results in creating an extra cell editing request - which results in incorrect closing the editing cell.
        // https://linear.app/y42/issue/PLA-1389/empty-folders-cannot-be-deleted-in-the-file-tree
        (window.document.activeElement as HTMLElement)?.blur?.();

        this.gridApi.startEditingCell({
          rowIndex: added.rowIndex,
          colKey: DEFAULT_AG_GRID_COLUMN_KEY,
        });
      }),
    );
  }

  /*private getNodeByAssetID(tree: AssetTreeNode, id: EntityID) {
    return findInTree(tree, (node) => node.asset?.$id === id);
  }

  private getNodeByDisplayPath(sourceDirectory: AssetTreeNode, displayPath: string, entityType?: EntityType) {
    return findInTree(sourceDirectory, (node) => {
      if (!entityType) {
        return getVisiblePathParts(node, true).join('/') === displayPath;
      }

      if (!node.asset) {
        return false;
      }

      return node.asset.entityType === entityType && node.asset.name === displayPath.split('/').pop();
    });
  }

  private setLineageFocusedMode(entityId: EntityID | undefined) {
    this.entityActionDelegationService.delegateAction({
      type: EntityActionType.SetLineageFocusedMode,
      payload: {
        entityId,
      },
    });
  }
  */
}
